import { TelephoneCountryCodesEnum } from '../../../../resources/base/models';
import { CountryCodesEnum } from '../../../../resources/base/models/country';
import { BooleanEnum } from '../../../../resources/base/models/enums';
import { initOptional } from '../../../../services/common/miscUtils';
import { DataStorageCustomDataType } from '../../../../services/redux/features/dataStorage.redux';
import { formatCityNameLettersAndSpaceOnly } from '../../utils/formatCityString';
import { apiDateToUiDate } from '../../utils/formatDate';
import {
  employmentsToInvolvementInCompanies,
  getCustomerRelations,
  getPersonalExpenseOfType,
  getPersonalIncomeOfType,
  InvolvementInCompanies,
} from '../utils';

type CustomerData =
  | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
  | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse;

export const getGeneralInfoDataFormInitialValues = (customerData: CustomerData) => {
  const _ = customerData;

  return {
    legalEntity: _.companyData?.entityLegalForm as CreditIntegration.Schemas.EntityLegalFormType | undefined,
    companyName: _.companyData?.name,
    apecode: _.companyData?.apecode,
    companyCreationDate: apiDateToUiDate(_.companyData?.foundationDate),
    mobilePhoneCountryCode: _.contactData?.mobilePhoneCountryCode,
    mobilePhone: _.contactData?.mobilePhoneNumber,
    telephonePhone: _.contactData?.telephoneNumber && `+33 ${_.contactData?.telephoneNumber}`,
    email: _.contactData?.emails?.[0].emailAddress.toLowerCase(),
  };
};

export type GeneralInfoDataFormType = ReturnType<typeof getGeneralInfoDataFormInitialValues>;

export const getAddressDataFormInitialValues = (customerData: CustomerData) => {
  const _ = customerData;

  return {
    addressLine1: _.addresses?.[0].addressLine1,
    optionalLine: _.addresses?.[0].optionalLine,
    zipCode: _.addresses?.[0].zipCode,
    city: _.addresses?.[0].city,
    country: _.addresses?.[0].countryCode ?? CountryCodesEnum.FR,
  };
};

export type AddressDataFormType = ReturnType<typeof getAddressDataFormInitialValues>;

export const getPersonalInfoDataFormInitialValues = (customerData: CustomerData) => {
  const _ = getCustomerRelations(customerData, 'CEO');

  return {
    gender: _?.gender,
    ceoFirstName: _?.firstName,
    ceoLastName: _?.lastName,
    ceoBirthName: _?.birthName,
    ceoDateOfBirth: apiDateToUiDate(_?.dateOfBirth),
    ceoNationality: _?.nationality ?? CountryCodesEnum.FR,
    ceoBirthCountry: _?.countryOfBirth ?? CountryCodesEnum.FR,
    ceoBirthPlaceZip: _?.placeOfBirthZipCode,
    ceoBirthPlaceCity: formatCityNameLettersAndSpaceOnly(_?.placeOfBirth),
  };
};

export type PersonalInfoDataFormType = ReturnType<typeof getPersonalInfoDataFormInitialValues>;

/**
 * - Only for soletrader.
 */
export const getAdditionalInfoDataFormInitialValues = (
  customerData: CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse
) => {
  const _ = getCustomerRelations(customerData, 'CEO');

  return {
    professionEntrepreneur: _?.employments?.[0]?.posFrOccupation,
    residenceStatus: _?.financialDisclosure?.housingSituation,
    maritalStatus: _?.maritalStatus,
    numberOfDependentChildren: _?.numberOfDependentChildren,
    netIncomeAmount: getPersonalIncomeOfType(_, 'PERSONAL_INCOME'),
    otherIncome: getPersonalIncomeOfType(_, 'ADDITIONAL'),
    monthlyPropertyCharge: getPersonalExpenseOfType(_, 'RENT'),
    otherMonthlyCosts: getPersonalExpenseOfType(_, 'ADDITIONAL'),
  };
};

export type AdditionalInfoDataFormType = ReturnType<typeof getAdditionalInfoDataFormInitialValues>;

export const getCompanyDataFormInitialValues = (
  creditApplicationData:
    | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
    | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse
) => {
  const involvementInCompanies = employmentsToInvolvementInCompanies(creditApplicationData);
  const isInvolvedInCompanies = Object.keys(involvementInCompanies)?.length > 0;

  return {
    oneCustomerHasCompany: isInvolvedInCompanies ? BooleanEnum.true : undefined,
    ...involvementInCompanies,
  } as {
    oneCustomerHasCompany: BooleanEnum;
  } & InvolvementInCompanies;
};

export type CompanyDataFormType = ReturnType<typeof getCompanyDataFormInitialValues>;

export const getStakeholderInfoDataFormInitialValues = (
  stakeholderContactData?: CustomerIntegration.Schemas.StakeholderContactsItem
) => {
  const stakeholder = stakeholderContactData;

  const stakeholderMobilePhoneCountryCode = stakeholder?.contactData?.mobilePhoneCountryCode
    ? // Ensure code is prefix with '+' sign.
      `+${stakeholder?.contactData?.mobilePhoneCountryCode}`.replace('++', '+')
    : undefined;

  return {
    gender: initOptional<CustomerIntegration.Schemas.GenderType>(stakeholder?.gender),
    ceoFirstName: initOptional<string>(stakeholder?.firstName),
    ceoLastName: initOptional<string>(stakeholder?.lastName),
    ceoBirthName: initOptional<string>(stakeholder?.birthName),
    ceoDateOfBirth: initOptional<string>(apiDateToUiDate(stakeholder?.dateOfBirth)),
    ceoNationality: stakeholder?.nationality || CountryCodesEnum.FR,
    ceoBirthCountry: stakeholder?.countryOfBirth?.countryCodeIso2 || CountryCodesEnum.FR,
    ceoBirthPlaceCity: initOptional<string>(stakeholder?.placeOfBirth || ''),
    ceoBirthPlaceZip: initOptional<string | null>(stakeholder?.placeOfBirthZipCode || ''),
    email: stakeholder?.contactData?.emails?.[0]?.emailAddress,
    mobilePhone: initOptional<string | null>(
      stakeholder?.contactData?.mobilePhoneNumber || stakeholder?.contactData?.mobilePhoneNumber
    ),
    mobilePhoneCountryCode:
      stakeholderMobilePhoneCountryCode ?? (TelephoneCountryCodesEnum['+33'] as keyof typeof TelephoneCountryCodesEnum),
  };
};

export type ExecutiveInfoDataFormType = ReturnType<typeof getStakeholderInfoDataFormInitialValues>;
export type SignatoryInfoDataFormType = ReturnType<typeof getStakeholderInfoDataFormInitialValues>;

export const getPaymentDataFormInitialValues = (
  customerData: CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse,
  dataStorageData: DataStorageCustomDataType
) => {
  const _ = customerData;

  return {
    bankAccountOwner: dataStorageData.bankAccountOwner,
    openingDate: dataStorageData.openingDate,
    iban: dataStorageData.iban,
    bic: dataStorageData.bic,
  };
};

export type PaymentDataFormType = ReturnType<typeof getPaymentDataFormInitialValues>;
